import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAppSettings } from '../components/providers/AppSettingsProvider';
import { getPageBackgroundGradient } from '../config/theme';
import type { ExploreInsightItem } from '../services/exploreService';
import insightStarIcon from '../assets/insight-star.png';

type InsightDetailLocationState = {
  item: ExploreInsightItem;
};

function isValidInsightDetailState(s: unknown): s is InsightDetailLocationState {
  if (!s || typeof s !== 'object') return false;
  const item = (s as Record<string, unknown>).item as ExploreInsightItem | undefined;
  if (!item || typeof item !== 'object') return false;
  if (item.status !== 'ready') return false;
  if (typeof item.insight_type !== 'string' || !item.insight_type.trim()) return false;
  if (typeof item.display_title !== 'string' || !item.display_title.trim()) return false;
  return true;
}

function clampSignals(signals: string[] | null): string[] {
  if (!signals?.length) return [];
  return signals.filter((s) => typeof s === 'string' && s.trim()).slice(0, 8);
}

const InsightDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, theme } = useAppSettings();
  const isDark = theme === 'dark';

  const state = isValidInsightDetailState(location.state) ? location.state : null;

  useEffect(() => {
    if (!state) {
      navigate('/library', { replace: true });
    }
  }, [state, navigate]);

  if (!state) return null;

  const { item } = state;
  const signals = clampSignals(item.signals);

  return (
    <div className="fixed inset-0 flex h-full w-full flex-col overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />

      <div className="relative z-10 flex h-full min-h-0 flex-col">
        <header
          className="relative flex flex-shrink-0 items-center justify-center px-4 pb-3"
          style={{ paddingTop: 'max(7vh, env(safe-area-inset-top, 0px))' }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`absolute left-2 rounded-xl p-2 transition-colors ${
              isDark ? 'text-white hover:bg-white/10' : 'text-gray-800 hover:bg-black/5'
            }`}
            aria-label={t('common.back')}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <h1
            className={`mx-12 text-center text-base font-medium leading-snug ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            {item.display_title}
          </h1>
        </header>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-5 pb-10">
          <div
            className={`mx-auto mt-2 max-w-sm rounded-[28px] p-7 text-center shadow-lg ${
              isDark ? 'bg-[#2b63b7]/70' : 'bg-sky-200/90'
            }`}
          >
            <div className="mx-auto mb-6 flex h-48 w-48 items-center justify-center rounded-2xl">
              <img
                src={insightStarIcon}
                alt=""
                className="h-48 w-48 select-none"
                draggable={false}
              />
            </div>

            <p className={`mx-auto max-w-[280px] text-2xl font-medium leading-snug ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {item.pattern ?? ''}
            </p>

            {typeof item.checkin_count === 'number' && typeof item.conversation_count === 'number' ? (
              <p className={`mx-auto mt-5 max-w-[300px] text-sm leading-relaxed ${isDark ? 'text-white/85' : 'text-gray-700'}`}>
                {t('library.knowYourself.insightDetail.basedOnPast30Days', {
                  checkins: String(item.checkin_count),
                  conversations: String(item.conversation_count),
                })}
              </p>
            ) : null}
          </div>

          <h2 className={`mt-10 text-center text-2xl font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
            What Lumi Observed
          </h2>

          <section className="mt-5" aria-label="Insight signals">
            <div className="flex gap-4 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch]">
              {signals.map((s, idx) => (
                <div
                  key={idx}
                  className={`w-[min(280px,72vw)] flex-shrink-0 rounded-2xl p-5 ${
                    isDark ? 'bg-[#1a4f9c]/75' : 'bg-white/80 shadow-sm shadow-black/5'
                  }`}
                >
                  <div
                    className={`mb-4 flex h-7 w-7 items-center justify-center rounded-full border text-sm font-semibold ${
                      isDark ? 'border-white/40 text-white' : 'border-gray-300 text-gray-700'
                    }`}
                    aria-hidden
                  >
                    {idx + 1}
                  </div>
                  <p className={`text-base font-medium leading-relaxed ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {s}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default InsightDetail;

